import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  CardContent
} from "@mui/material";
import { tokens } from "../../theme";
import Card from "./Card";

export interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
}

interface Row {
  [key: string]: any;
}

interface CustomItemListProps {
  columns: Column[];
  rows: Row[];
  maxHeight?: number;  // Optional now
  handleAction?: (row: Row, actionType: string) => void;
  uniqueValue?: string;
}

const CustomItemList = ({
  columns,
  rows,
  maxHeight,
  handleAction,
  uniqueValue
}: CustomItemListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);

  const renderListView = () => (
    <List
      sx={{
        bgcolor: colors.grey[900],
        borderRadius: 3,
        maxHeight: maxHeight,
        overflowY: maxHeight ? "auto" : "visible",
        position: "relative",
      }}
    >
      <ListItem
        sx={{
          py: 0,
          top: 0,
          zIndex: 1,
          backgroundColor: colors.grey[900],
          borderBottom: `1px solid ${colors.grey[800]}`,
        }}
      >
        <ListItemButton sx={{ padding: 1, borderRadius: 2, fontSize: 14, display: "flex" }}>
          {columns.map((column, index) => (
            <ListItemText
              key={index}
              sx={{
                flex: column.minWidth ? `0 0 ${column.minWidth}px` : `1 1 0`,
                minWidth: column.minWidth,
                maxWidth: column.minWidth,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <Typography
                fontWeight="bold"
                color={theme.palette.mode === "dark" ? colors.info[300] : colors.grey[500]}
              >
                {column.label}
              </Typography>
            </ListItemText>
          ))}
        </ListItemButton>
      </ListItem>
      
      {rows.map((row, rowIndex) => (
        <ListItem key={ (uniqueValue && row[uniqueValue]) || rowIndex} sx={{ py: 0 }}>
          <ListItemButton sx={{ pl: 1, pr: 1, borderRadius: 2, display: "flex" }}>
            {columns.map((column, colIndex) => (
              <ListItemText
                key={colIndex}
                sx={{
                  width: `${100 / columns.length}%`,
                  minWidth: column.minWidth,
                  maxWidth: column.minWidth,
                  pr: 2,
                  textAlign: column.align || "left",
                }}
              >
                {column.id === "action" && handleAction ? (
                  <IconButton onClick={() => handleAction(row, column.id)}>
                    {row[column.id]}
                  </IconButton>
                ) : (
                  row[column.id]
                )}
              </ListItemText>
            ))}
          </ListItemButton>
          {
            row.isStrikethrough &&
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 20,
                right: 65,
                borderTop: '2px solid red',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          }
        </ListItem>
      ))}
    </List>
  );

  const renderCardView = (row: any) => (
    <Card
      sx={{
          padding : 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: colors.grey[800],
          overflow: "hidden",
          position: "relative",
        }}
      >        
        <Grid container spacing={1}>
          {columns.map((column, colIndex) => (
            <Grid item xs={12} key={colIndex}>
              <Typography
                variant="subtitle2"
                color={colors.info[300]}
                sx={{ minWidth: "150px", fontWeight: "bold" }}
              >
                {column.label}
              </Typography>
              <Typography
                color={colors.grey[300]}
                sx={{
                  gap: "8px",
                  flexWrap: "wrap",
                  mt:1,
                  textAlign: column.align || "left", 
                }}
              >
                {column.id === "action" && handleAction ? (
                  <IconButton onClick={() => handleAction(row, column.id)} color="primary">
                    {row[column.id]}
                  </IconButton>
                ) : (
                  row[column.id]
                )}
              </Typography>
            </Grid>
          ))}
        </Grid>
    </Card>
  );
  
  

  return (
    <Box>
      {isMobile ? rows.map((row) => renderCardView(row)) : renderListView()}
    </Box>
  );
};

export default CustomItemList;
